import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  selectAllConversations,
  selectSelectedConversation,
  setSelectedConversation,
  selectIsSelectionMode,
  selectSelectedConversationIds,
  toggleConversationSelection,
  performBulkAction,
  fetchConversations,
  User
} from "../../store/slices/conversationsSlice";
import "./whatsapp.css";
import { Logger } from "../../utils/logger";
import StatusLabelChips from "./components/StatusLabelChips";
import InitialsAvatar from "./components/InitialsAvatar";
import { Checkbox } from "antd";
import { MoreVertical, Users2, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { hasFullAdminAccess } from "../../utils/roleUtils";
import styled from "styled-components";

// ===== Styled components (same pattern as ConversationBulkActionsBar) =====

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const ModalContent = styled.div`
  position: relative;
  z-index: 1101;
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 400px;
  max-width: 90vw;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  font-family: 'Gilroy-SemiBold', sans-serif;
`;

const ModalMessage = styled.p`
  margin: 0 0 24px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  font-family: 'Gilroy-Medium', sans-serif;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const SelectWrapper = styled.div`
  margin-bottom: 24px;
  select {
    width: 100%;
    height: 40px;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    font-family: 'Gilroy-Medium', sans-serif;
  }
`;

const Button = styled.button<{ $isWarning?: boolean; $isPrimary?: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Gilroy-Medium', sans-serif;

  ${props => props.$isWarning ? `
    background: #ef4444;
    color: white;
    &:hover { background: #dc2626; }
  ` : props.$isPrimary ? `
    background: #3FAEFD;
    color: white;
    &:hover { background: #2c9ee0; }
  ` : `
    background: #f1f5f9;
    color: #64748b;
    &:hover { background: #e2e8f0; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ===== Component =====

interface InboxListReduxProps {
  users?: any[];
  setSelectedUser?: (user: User) => void;
  selectedUser?: User | null;
  onConnect?: (user: User) => void;
}

interface Employee {
  value: string;
  label: string;
}

const InboxListRedux: React.FC<InboxListReduxProps> = (props) => {
  const dispatch = useAppDispatch();
  const conversations = useAppSelector(selectAllConversations);
  const selectedConversationFromRedux = useAppSelector(selectSelectedConversation);

  const isSelectionMode = useAppSelector(selectIsSelectionMode);
  const selectedIds = useAppSelector(selectSelectedConversationIds);

  const userPreferences = useAppSelector((state: any) =>
    state.rootReducers?.user?.userPreferences
  );

  // Get employee list for assignment (same logic as ConversationBulkActionsBar)
  const employees: Employee[] = useAppSelector((state: any) => {
    const userRole = state?.rootReducers?.userData?.userDetails?.role?.name;
    const subordinateData = state?.rootReducers?.userData?.userDetails?.subordinateEmployees;
    const userDetails = state?.rootReducers?.userData?.userDetails;

    const formatName = (firstName: string, lastName: string | null) => {
      if (!firstName) return '';
      return lastName ? `${firstName} ${lastName}`.trim() : firstName.trim();
    };

    if (hasFullAdminAccess(userDetails)) {
      const orgEmployees = userDetails?.organizationEmployee || [];
      return orgEmployees.map((item: any) => ({
        value: item?._id,
        label: formatName(item?.firstName, item?.lastName)
      }));
    }

    const reportTo = subordinateData?.reportTo || [];
    const teamMembers = subordinateData?.teamMembers || [];
    const orgMembers = subordinateData?.organizationMember || [];

    let filteredUsers: any[] = [];
    if (userRole === 'employee') {
      filteredUsers = [{
        firstName: userDetails?.firstName || '',
        lastName: userDetails?.lastName || '',
        _id: userDetails?._id,
      }];
    } else if (userRole === 'manager' || userRole === 'team_lead') {
      const allSubordinates = [...reportTo, ...teamMembers];
      filteredUsers = allSubordinates.filter((user: any, index: number, array: any[]) =>
        array.findIndex((u: any) => u._id === user._id) === index
      );
    } else {
      const allUsers = [...reportTo, ...teamMembers, ...orgMembers];
      filteredUsers = allUsers.filter((user: any, index: number, array: any[]) =>
        array.findIndex((u: any) => u._id === user._id) === index
      );
    }

    return filteredUsers.map((item: any) => ({
      value: item?._id,
      label: formatName(item?.firstName, item?.lastName)
    }));
  });

  // Action menu state
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [assignModalUser, setAssignModalUser] = useState<any>(null);
  const [deleteModalUser, setDeleteModalUser] = useState<any>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [actionLoading, setActionLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActiveMenuId(null);
      }
    };
    if (activeMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeMenuId]);

  const conversationData = props.users || conversations;
  const selectedConversation = props.selectedUser || selectedConversationFromRedux;

  Logger.log("InboxListRedux rendered", {
    conversationsCount: conversationData?.length,
    selectedId: selectedConversation?.id
  });

  const getDate = (date: any) => {
    const inputDate: any = moment(date);
    if (inputDate.isSame(moment(), "day")) {
      return inputDate.format("hh:mm A");
    }
    if (inputDate.isSame(moment().subtract(1, "days"), "day")) {
      return "Yesterday";
    }
    if (
      inputDate.isAfter(moment().subtract(7, "days")) &&
      !inputDate.isSame(moment(), "day") &&
      !inputDate.isSame(moment().subtract(1, "days"), "day")
    ) {
      return inputDate.format("dddd");
    }
    return inputDate.format("MMM D, YYYY");
  };

  const handleCheckboxChange = (e: any, userId: string) => {
    e.stopPropagation();
    dispatch(toggleConversationSelection(userId));
  };

  const handleSelectUser = (user: User) => {
    if (isSelectionMode) {
      dispatch(toggleConversationSelection(user._id));
      return;
    }
    if (props.onConnect) {
      props.onConnect(user);
    } else if (props.setSelectedUser) {
      props.setSelectedUser(user);
    } else {
      dispatch(setSelectedConversation(user));
    }
  };

  // ===== Action handlers =====

  const handleAssignClick = (user: any) => {
    setActiveMenuId(null);
    setAssignModalUser(user);
    setSelectedEmployee('');
  };

  const handleAssignConfirm = async () => {
    if (!assignModalUser || !selectedEmployee) return;
    setActionLoading(true);
    try {
      // Use same bulk action approach as ConversationBulkActionsBar
      await dispatch(performBulkAction({
        action: 'assign',
        conversationIds: [assignModalUser._id],
        assignToIds: [selectedEmployee],
      })).unwrap();
      toast.success('Conversation assigned successfully');
      dispatch(fetchConversations({ page: 1, refresh: true }));
    } catch (error: any) {
      Logger.log('Assign error:', error);
      toast.error(error || 'Failed to assign conversation');
    }
    setActionLoading(false);
    setAssignModalUser(null);
  };

  const handleDeleteClick = (user: any) => {
    setActiveMenuId(null);
    setDeleteModalUser(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalUser) return;
    setActionLoading(true);
    try {
      // Use same bulk action approach as ConversationBulkActionsBar
      await dispatch(performBulkAction({
        action: 'delete_conversation',
        conversationIds: [deleteModalUser._id],
      })).unwrap();
      toast.success('Conversation deleted');
      dispatch(fetchConversations({ page: 1, refresh: true }));
    } catch (error: any) {
      Logger.log('Delete error:', error);
      toast.error(error || 'Failed to delete conversation');
    }
    setActionLoading(false);
    setDeleteModalUser(null);
  };

  const renderMessagePreview = (user: any) => {
    const messageType = user?.lastMessage?.messageType || 'text';
    switch(messageType) {
      case 'reaction':
        const emoji = user?.lastMessage?.message?.emoji;
        return emoji ? `Reacted with ${emoji}` : 'Reacted to a message';
      case 'image':
        return '📷 Photo';
      case 'video':
        return '🎥 Video';
      case 'document':
        const filename = user?.lastMessage?.message?.filename ||
                        user?.msg?.filename || 'Document';
        return `📄 ${filename}`;
      case 'audio':
        return '🔊 Audio';
      case 'template':
        const templateText = user?.lastMessage?.message?.body?.text?.body ||
                          'Template message';
        return templateText;
      case 'text':
      default:
        if (typeof user?.msg === 'string') return user.msg;
        else if (user?.msg?.text?.body) return user.msg.text.body;
        else if (user?.msg?.body) {
          if (typeof user.msg.body === 'string') return user.msg.body;
          else if (user.msg.body?.text?.body) return user.msg.body.text.body;
        }
        return user?.lastMessage?.message?.body || 'Message';
    }
  };

  // ===== Modal portals (rendered on document.body to escape overflow stacking context) =====

  const renderAssignModal = () => {
    if (!assignModalUser) return null;
    return ReactDOM.createPortal(
      <ModalOverlay onClick={() => !actionLoading && setAssignModalUser(null)}>
        <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalTitle>Assign Conversation</ModalTitle>
          <ModalMessage>
            Select a team member to assign this conversation to:
          </ModalMessage>
          <SelectWrapper>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="form-select"
              disabled={actionLoading}
            >
              <option value="">Select Team Member</option>
              {employees.map((emp) => (
                <option key={emp.value} value={emp.value}>
                  {emp.label}
                </option>
              ))}
            </select>
          </SelectWrapper>
          <ModalActions>
            <Button
              onClick={() => setAssignModalUser(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              $isPrimary
              onClick={handleAssignConfirm}
              disabled={actionLoading || !selectedEmployee}
            >
              {actionLoading ? 'Assigning...' : 'Assign'}
            </Button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>,
      document.body
    );
  };

  const renderDeleteModal = () => {
    if (!deleteModalUser) return null;
    return ReactDOM.createPortal(
      <ModalOverlay onClick={() => !actionLoading && setDeleteModalUser(null)}>
        <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalTitle>Delete Conversation</ModalTitle>
          <ModalMessage>
            Are you sure you want to delete this conversation? This will remove it from your inbox but keep the chat history.
          </ModalMessage>
          <ModalActions>
            <Button
              onClick={() => setDeleteModalUser(null)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              $isWarning
              onClick={handleDeleteConfirm}
              disabled={actionLoading}
            >
              {actionLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>,
      document.body
    );
  };

  return (
    <>
      {conversationData?.map((element: any, idx: any) => {
        return (
          <div key={idx}>
            {element?.data?.map((user: any, index: any) => {
              let isSelected = user?.id === selectedConversation?.id;
              const isChecked = selectedIds.includes(user._id);
              const isMenuOpen = activeMenuId === user._id;
              return (
                <li className="mx-2 mb-2" key={index}>
                  <div
                    className={`item chat-item-row ${isSelected ? "active_button" : ""} ${isChecked ? "bulk-selected" : ""}`}
                    style={{
                      background: isChecked ? "rgba(37, 99, 235, 0.05)" : "transparent",
                      borderBottom: "1px solid #eee",
                      position: "relative",
                    }}
                    onClick={() => handleSelectUser(user)}
                  >
                    {/* Checkbox for bulk selection */}
                    {isSelectionMode && (
                      <div
                        style={{
                          marginRight: "8px",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => handleCheckboxChange(e, user._id)}
                        />
                      </div>
                    )}
                    <InitialsAvatar
                      name={user?.name}
                      phone={user?.mobile}
                      size={40}
                      style={{
                        marginRight: "8px",
                      }}
                    />
                    <div className="flex-fill d-flex flex-column">
                      <div className="d-flex mb-1">
                        <div className="mob">
                          {user?.name ? (
                            <span
                              className={`d-block ${isSelected ? "text-light" : ""}`}
                              style={{
                                fontSize: 14,
                                maxWidth: 160,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {user?.name ?? "No Name"}
                            </span>
                          ) : null}

                          {!user?.name ? (
                            <span
                              className={`d-block ${isSelected ? "text-light" : ""}`}
                              style={{ fontSize: 14 }}
                            >
                              {user?.mobile}
                            </span>
                          ) : null}
                        </div>
                        <span
                          className={`time ${isSelected ? "text-light" : ""}`}
                        >
                          {getDate(user?.lastMessageAt)}
                        </span>
                      </div>
                      <div className="d-flex">
                        <div className="ellipsisTxt fm-gilroy">
                          <span
                            style={
                              isSelected
                                ? { color: "#fff" }
                                : { color: "#00000080" }
                            }
                          >
                            {user?.msgStatus === "delivered" ? (
                              <i className="me-1">
                                <svg viewBox="0 0 16 11" height="11" width="16" preserveAspectRatio="xMidYMid meet" fill="none">
                                  <path d="M11.0714 0.652832C10.991 0.585124 10.8894 0.55127 10.7667 0.55127C10.6186 0.55127 10.4916 0.610514 10.3858 0.729004L4.19688 8.36523L1.79112 6.09277C1.7488 6.04622 1.69802 6.01025 1.63877 5.98486C1.57953 5.95947 1.51817 5.94678 1.45469 5.94678C1.32351 5.94678 1.20925 5.99544 1.11192 6.09277L0.800883 6.40381C0.707784 6.49268 0.661235 6.60482 0.661235 6.74023C0.661235 6.87565 0.707784 6.98991 0.800883 7.08301L3.79698 10.0791C3.94509 10.2145 4.11224 10.2822 4.29844 10.2822C4.40424 10.2822 4.5058 10.259 4.60313 10.2124C4.70046 10.1659 4.78086 10.1003 4.84434 10.0156L11.4903 1.59863C11.5623 1.5013 11.5982 1.40186 11.5982 1.30029C11.5982 1.14372 11.5348 1.01888 11.4078 0.925781L11.0714 0.652832ZM8.6212 8.32715C8.43077 8.20866 8.2488 8.09017 8.0753 7.97168C7.99489 7.89128 7.8891 7.85107 7.75791 7.85107C7.6098 7.85107 7.4892 7.90397 7.3961 8.00977L7.10411 8.33984C7.01947 8.43717 6.97715 8.54508 6.97715 8.66357C6.97715 8.79476 7.0237 8.90902 7.1168 9.00635L8.1959 10.0791C8.33132 10.2145 8.49636 10.2822 8.69102 10.2822C8.79681 10.2822 8.89838 10.259 8.99571 10.2124C9.09304 10.1659 9.17556 10.1003 9.24327 10.0156L15.8639 1.62402C15.9358 1.53939 15.9718 1.43994 15.9718 1.32568C15.9718 1.1818 15.9125 1.05697 15.794 0.951172L15.4386 0.678223C15.3582 0.610514 15.2587 0.57666 15.1402 0.57666C14.9964 0.57666 14.8715 0.635905 14.7657 0.754395L8.6212 8.32715Z" fill="currentColor"></path>
                                </svg>
                              </i>
                            ) : user?.msgStatus === "read" ? (
                              <i className="me-1" style={{ color: "#61bbfb" }}>
                                <svg viewBox="0 0 16 11" height="11" width="16" preserveAspectRatio="xMidYMid meet" fill="none">
                                  <path d="M11.0714 0.652832C10.991 0.585124 10.8894 0.55127 10.7667 0.55127C10.6186 0.55127 10.4916 0.610514 10.3858 0.729004L4.19688 8.36523L1.79112 6.09277C1.7488 6.04622 1.69802 6.01025 1.63877 5.98486C1.57953 5.95947 1.51817 5.94678 1.45469 5.94678C1.32351 5.94678 1.20925 5.99544 1.11192 6.09277L0.800883 6.40381C0.707784 6.49268 0.661235 6.60482 0.661235 6.74023C0.661235 6.87565 0.707784 6.98991 0.800883 7.08301L3.79698 10.0791C3.94509 10.2145 4.11224 10.2822 4.29844 10.2822C4.40424 10.2822 4.5058 10.259 4.60313 10.2124C4.70046 10.1659 4.78086 10.1003 4.84434 10.0156L11.4903 1.59863C11.5623 1.5013 11.5982 1.40186 11.5982 1.30029C11.5982 1.14372 11.5348 1.01888 11.4078 0.925781L11.0714 0.652832ZM8.6212 8.32715C8.43077 8.20866 8.2488 8.09017 8.0753 7.97168C7.99489 7.89128 7.8891 7.85107 7.75791 7.85107C7.6098 7.85107 7.4892 7.90397 7.3961 8.00977L7.10411 8.33984C7.01947 8.43717 6.97715 8.54508 6.97715 8.66357C6.97715 8.79476 7.0237 8.90902 7.1168 9.00635L8.1959 10.0791C8.33132 10.2145 8.49636 10.2822 8.69102 10.2822C8.79681 10.2822 8.89838 10.259 8.99571 10.2124C9.09304 10.1659 9.17556 10.1003 9.24327 10.0156L15.8639 1.62402C15.9358 1.53939 15.9718 1.43994 15.9718 1.32568C15.9718 1.1818 15.9125 1.05697 15.794 0.951172L15.4386 0.678223C15.3582 0.610514 15.2587 0.57666 15.1402 0.57666C14.9964 0.57666 14.8715 0.635905 14.7657 0.754395L8.6212 8.32715Z" fill="currentColor"></path>
                                </svg>
                              </i>
                            ) : null}

                            {renderMessagePreview(user)}
                          </span>

                          {user?.unReadCount ? (
                            <div className="unread_container">
                              <span>{user?.unReadCount}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Status and Label Chips */}
                      {((user?.status?.length ?? 0) > 0 || (user?.label?.length ?? 0) > 0) && (
                        <StatusLabelChips
                          status={user?.status}
                          labels={user?.label}
                          userPreferences={userPreferences}
                          maxVisible={3}
                          compact={false}
                        />
                      )}
                    </div>

                    {/* Action menu trigger - visible on hover */}
                    {!isSelectionMode && (
                      <div
                        className="chat-action-trigger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuId(isMenuOpen ? null : user._id);
                        }}
                      >
                        <MoreVertical size={16} />
                      </div>
                    )}

                    {/* Action dropdown menu */}
                    {isMenuOpen && (
                      <div className="chat-action-menu" ref={menuRef} onClick={(e) => e.stopPropagation()}>
                        <button
                          className="chat-action-menu-item"
                          onClick={() => handleAssignClick(user)}
                        >
                          <Users2 size={14} />
                          <span>Assign</span>
                        </button>
                        <button
                          className="chat-action-menu-item chat-action-danger"
                          onClick={() => handleDeleteClick(user)}
                        >
                          <Trash2 size={14} />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </div>
        );
      })}

      {/* Portaled modals - rendered on document.body to escape overflow stacking context */}
      {renderAssignModal()}
      {renderDeleteModal()}
    </>
  );
};

export default InboxListRedux;
